import React, {useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../hooks/use-is-mounted";
import {DialogTitle, Divider, SvgIcon, useTheme} from "@mui/material";
import Dialog from "../../base/dialog";
import {Col, Row} from "reactstrap";
import Form from "../../base/form";
import Button from "../../base/button/button";
import {exportCSVFile, formatMoney} from "../../../../core/services/utils/utils";
import LineChart from "../../base/chart/line-chart";
import {ReactComponent as Excel} from "../../../../assets/images/excel.svg";
import Api from "../../../../core/services/api/api";
import {MuiRFF, Yup} from '../../form';
import DateUtils from "../../../../core/services/date-utils";
import ToastService from "../../../../core/services/toast";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import useAccessLevels from "../../../hooks/use-access-levels";

/**
 * The type of the moneris transactions for the timeline dialog.
 */
const TransactionTimelineTypes = {
    purchase: 'Purchase',
    refund: 'Refund',
    preAuthorization: 'PreAuthorization',
    preAuthCancellation: 'PreAuthCancellation',
}

const AccountingTransactionsChartDialog = ({open, setOpen}) => {
    const [items, setItems] = useState([]);
    const [data, setData] = useState({});
    const [loading, setLoading] = useState(false);
    const isMounted = useIsMounted();
    const [filters, setFilters] = useState({});
    const [exporting, setExporting] = useState(false);
    const operatorAccessLevels = useAccessLevels();
    const canExportOnlinePaymentTransactions = operatorAccessLevels.packages.sales.exportOnlinePaymentTransactions;

    /**
     * Listens to the changes of filters,
     * Calls the function for getting moneris transactions with each change
     */
    useEffect(() => {
        setExporting(false);
        if (!open)
            return;
        getMonerisTransactions().then()
    }, [filters, open])

    /**
     * Fetches the moneris transactions from server:
     * - Calls the api regarding this action
     * - If the api call was successful, sets the [data] in the inner state with results
     *
     * @return {Promise<void>}
     */
    const getMonerisTransactions = async () => {
        setLoading(true);
        const forApi = {
            period: {
                startDateTime: filters[formValues.startDate] ?? null,
                endDateTime: filters[formValues.endDate] ?? null,
            }
        }
        const response = await Api.searchOnlinePaymentReportTimeline(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setItems(response?.data ?? []);
            setData(prepareTimelineData(response.data ?? []));
        }
        setLoading(false);

    }

    /**
     * Prepares the timeline data of the state by aggregating the data into our own transaction types.
     * @param list
     * @return {{totalPreAuthCancellation: number, totalPreAuth: number, chartData: {[p: string]: *[]}, totalPayment: number, totalRefund: number}}
     */
    const prepareTimelineData = (list) => {
        const aggregatedTotals = {
            totalPayment: 0,
            totalRefund: 0,
            totalPreAuth: 0,
            totalPreAuthCancellation: 0,
        }
        const chartData = Object.fromEntries(Object.values(TransactionTimelineTypes).map(e => [e, []]));
        for (const item of list) {
            chartData[item.type]?.push(item);
            switch (item.type) {
                case TransactionTimelineTypes.purchase:
                    aggregatedTotals.totalPayment += (item.amount ?? 0);
                    break;
                case TransactionTimelineTypes.refund:
                    aggregatedTotals.totalRefund += (item.amount ?? 0);
                    break;
                case TransactionTimelineTypes.preAuthorization:
                    aggregatedTotals.totalPreAuth += (item.amount ?? 0);
                    break;
                case TransactionTimelineTypes.preAuthCancellation:
                    aggregatedTotals.totalPreAuthCancellation += (item.amount ?? 0);
                    break;
                default:
                    // ignoring the unknown type.
                    break;
            }
        }

        return {
            ...aggregatedTotals,
            chartData: chartData,
        }
    }

    /**
     * Handles the changes in the start date and end date in the search section
     * Sets the [filters] in the inner state
     * @param values
     */
    const onDateFiltered = (values) => {
        setFilters(values);
    }

    /**
     * Generates a csv report for the transaction items
     */
    const exportToCsv = () => {
        setExporting(true);
        const exportTitle = `Online Transactions Timeline Information`
        const toBeExported = items.map((transaction, index) => {
            return {
                '': index + 1,
                'Amount': formatMoney(transaction?.amount?.toString()) ?? '',
                'Type': transaction?.type,
                'Date': DateUtils.serverToLocal(transaction?.date).format(DateUtils.dateTimeFormat)
            }
        });
        if (!toBeExported.length) {
            setExporting(false);
            ToastService.warning({message: "No transactions found!"});
            return;
        }
        exportCSVFile(toBeExported, exportTitle);
        setExporting(false);
    }

    return (
        <Dialog
            open={open}
            setOpen={setOpen}
            maxWidth={'lg'}
            className={'bound-dialog moneris-transactions-dialog'}
            scroll={'paper'}
            aria-labelledby="scroll-dialog-title"
        >
            <DialogTitle id={'scroll-dialog-title'}>
                <p className={`text-lg text-color-1 my-3 font-weight-bold text-center`}>
                    Online Transactions Analytics
                </p>
                <Divider className='divider'/>
            </DialogTitle>
            <div className={'w-100 d-flex justify-content-between my-3 pr-3'}>
                <div/>
                {
                    canExportOnlinePaymentTransactions &&
                    <Button
                        className={'d-flex justify-content-center align-items-center'}
                        disabled={exporting || loading}
                        variant={'outlined'}
                        color={'primary'}
                        onClick={exportToCsv}
                    >
                        <SvgIcon className={`icon normal`} component={Excel}/>
                        <p className='ml-2'>
                            {
                                exporting
                                    ? "Exporting..."
                                    : " Convert to Excel"
                            }
                        </p>
                    </Button>
                }
            </div>
            <div className={'w-100 justify-content-center align-items-center px-3'}>
                <SearchSection submit={onDateFiltered} loading={loading}/>
            </div>
            <div className={'w-100 justify-content-center align-items-center px-3'}>
                <ChartSection dataset={data?.chartData ?? {}}/>
            </div>
            <Row className={'d-flex align-items-center justify-content-center mb-5 mt-3'}>
                <div className={'indicator'}>
                    <div className={'indicator-number'}>
                        <p>{formatMoney(data?.totalPayment ?? '0')}</p>
                    </div>
                    <p className={'indicator-label'}>Total Payment</p>
                </div>
                <div className={'indicator'}>
                    <div className={'indicator-number'}>
                        <p>{formatMoney(data?.totalRefund ?? '0')}</p>
                    </div>
                    <p className={'indicator-label'}>Total Refund</p>
                </div>
                <div className={'indicator'}>
                    <div className={'indicator-number'}>
                        <p>{formatMoney(data?.totalPreAuth ?? '0')}</p>
                    </div>
                    <p className={'indicator-label'}>Total Pre-Auth</p>
                </div>
                <div className={'indicator'}>
                    <div className={'indicator-number'}>
                        <p>{formatMoney(data?.totalPreAuthCancellation ?? '0')}</p>
                    </div>
                    <p className={'indicator-label'}>Total Pre-Auth cancellation</p>
                </div>
            </Row>
        </Dialog>
    );
}

const formValues = {
    startDate: 'startDate',
    endDate: 'endDate',
}

const schema = Yup.object().shape({
    [formValues.startDate]: Yup.mixed()
        .nullable()
        .validDate(ValidateMessages.incorrect)
        .disableFutureDate(ValidateMessages.maxDate('today'), 'day')
        .maxDate(Yup.ref(formValues.endDate), ValidateMessages.lessThan("End Date")),
    [formValues.endDate]: Yup.mixed()
        .nullable()
        .validDate(ValidateMessages.incorrect)
        .disableFutureDate(ValidateMessages.maxDate('today'), 'day')
        .minDate(Yup.ref(formValues.startDate), ValidateMessages.greaterThan("End Date")),
});

const validate = MuiRFF.makeValidate(schema);
const required = MuiRFF.makeRequired(schema);

/**
 * Search section component for sorting the results
 * @param submit
 * @param loading
 * @return {JSX.Element}
 * @constructor
 */
const SearchSection = ({submit, loading}) => {

    const onSubmit = (values) => {
        submit({
            [formValues.startDate]: values[formValues.startDate] ? DateUtils.UTCToServer(values[formValues.startDate]).format(DateUtils.withoutOffsetFormat) : undefined,
            [formValues.endDate]: values[formValues.endDate] ? DateUtils.UTCToServer(values[formValues.endDate]).format(DateUtils.withoutOffsetFormat) : undefined,
        })
    }

    return (
        <Col xs={12}>
            <Form
                className='w-100 mt-3'
                onSubmit={onSubmit}
                validate={validate}
                initialValues={{}}
                render={({values}) => {
                    return (
                        <div className={'d-flex flex-wrap w-100 align-items-center'}>
                            <Col xs={12} md={6} lg={5} className={'mb-4'}>
                                <MuiRFF.DatePicker
                                    name={formValues.startDate}
                                    label={"Start Date"}
                                    required={required[formValues.startDate]}
                                    maxDate={values[formValues.endDate]}
                                    disableFuture
                                />
                            </Col>
                            <Col xs={12} md={6} lg={5} className={'mb-4'}>
                                <MuiRFF.DatePicker
                                    name={formValues.endDate}
                                    label={"End Date"}
                                    required={required[formValues.endDate]}
                                    minDate={values[formValues.startDate]}
                                    disableFuture
                                />
                            </Col>
                            <Col xs={12} lg={2} className={'mb-4 d-flex'}>
                                <Button
                                    disabled={loading}
                                    className='px-8 m-auto'
                                    type={'submit'}
                                    variant={'contained'}
                                    color={'primary'}
                                >
                                    {loading ? 'Searching...' : 'Search'}
                                </Button>
                            </Col>
                        </div>
                    );
                }}
            />
        </Col>
    );
}

const ChartSection = ({dataset}) => {
    const theme = useTheme();
    const chartColors = useMemo(() => Object.values(theme.palette['app-specific'].chart), [theme.palette])

    /**
     * Compares strings in format of 'MM/DD' based on the date they represent
     * @param dateStringOne
     * @param dateStringTwo
     * @return {number}
     */
    const dateStringCompare = (dateStringOne, dateStringTwo) => {
        if (parseInt(dateStringOne.split('/')[1]) < parseInt(dateStringTwo.split('/')[1]))
            return -1;
        if (parseInt(dateStringOne.split('/')[1]) === parseInt(dateStringTwo.split('/')[1]) && parseInt(dateStringOne.split('/')[0]) < parseInt(dateStringTwo.split('/')[0]))
            return -1;
        if (parseInt(dateStringOne.split('/')[1]) === parseInt(dateStringTwo.split('/')[1]) && parseInt(dateStringOne.split('/')[0]) === parseInt(dateStringTwo.split('/')[0]))
            return 0;
        return 1;
    }

    /**
     * Aggregates data based on date
     * @param data
     * @return {{}}
     */
    const getDatasetData = (data = []) => {
        const aggregated = {};
        data?.map(e => ({date: DateUtils.serverToLocal(e.date).format("DD/MM"), amount: e?.amount ?? 0}))
            ?.sort((a, b) => dateStringCompare(a?.date, b?.date))
            ?.forEach(record => {
                aggregated[record.date] = !!aggregated[record.date]
                    ? parseFloat(aggregated[record.date]) + parseFloat((record?.amount ?? 0))
                    : parseFloat((record?.amount ?? 0))
            })
        return aggregated;
    }

    /**
     * Memo version of chart data passed to the
     * @type {{datasets: *[], labels: *[]}}
     */
    const chartData = useMemo(() => {
        const chartData = {
            datasets: [],
            labels: [],
        }

        Object.entries(dataset).forEach(([key, list]) => {
            const data = getDatasetData(list);
            let label;
            let color;
            switch (key) {
                case TransactionTimelineTypes.purchase:
                    label = 'Purchase';
                    color = chartColors[0];
                    break;
                case TransactionTimelineTypes.refund:
                    label = 'Refund';
                    color = chartColors[1];
                    break;
                case TransactionTimelineTypes.preAuthorization:
                    label = 'Pre-Auth';
                    color = chartColors[2];
                    break;
                case TransactionTimelineTypes.preAuthCancellation:
                    label = 'Pre-Auth Cancellation';
                    color = chartColors[3];
                    break;
                default:
                    // if it is not one of our known types, we just ignore this dataset.
                    return
            }
            chartData.datasets.push({
                data: data,
                label: label,
                formatValue: formatMoney,
                borderColor: color,
                pointBackgroundColor: color,
                pointBorderColor: color,
                borderWidth: 1,
                cubicInterpolationMode: 'monotone',
            });
            chartData.labels = [...new Set([...chartData.labels, ...Object.keys(data)])].sort((a, b) => dateStringCompare(a, b));
        });
        return chartData;

    }, [chartColors, dataset]);

    return (
        <>
            <Col xs={12}
                 className={'my-4  transition mx-auto bar-chart'}>
                <LineChart height={window.innerHeight / 2.5} data={chartData}/>
            </Col>
        </>
    );
}

export default AccountingTransactionsChartDialog;
